import { useState } from 'react';
import Layout from '../../components/feature/Layout';
import { useReveal } from '../../hooks/useReveal';
import { galleryItems, type GalleryCategory } from '../../mocks/gallery';
import { useTranslation } from 'react-i18next';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const gallerySchema = {
  '@context': 'https://schema.org',
  '@type': 'ImageGallery',
  '@id': `${siteUrl}/gallery`,
  url: `${siteUrl}/gallery`,
  name: 'Gallery | KV Trade Alliance — Korea-Vietnam Trade Missions',
  description:
    'Visual stories from KV Trade Alliance trade missions, port operations, and partnership events across Seoul and Ho Chi Minh City.',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Gallery', item: `${siteUrl}/gallery` },
    ],
  },
};

const filters: { labelKey: string; value: GalleryCategory }[] = [
  { labelKey: 'galleryPage.filter.all', value: 'all' },
  { labelKey: 'galleryPage.filter.korea', value: 'korea' },
  { labelKey: 'galleryPage.filter.vietnam', value: 'vietnam' },
  { labelKey: 'galleryPage.filter.events', value: 'events' },
];

export default function GalleryPage() {
  const ref = useReveal();
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<GalleryCategory>('all');
  const [lightbox, setLightbox] = useState<null | (typeof galleryItems)[0]>(null);

  const filtered = activeFilter === 'all' ? galleryItems : galleryItems.filter(g => g.category === activeFilter);

  const activeFilterLabel = activeFilter === 'all'
    ? t('galleryPage.allPhotos')
    : t(filters.find(f => f.value === activeFilter)?.labelKey ?? '');

  return (
    <Layout>
      <title>{t('page.title.gallery')}</title>
      <meta
        name="description"
        content="Visual stories from KV Trade Alliance trade missions, port operations, and partnership events across Seoul and Ho Chi Minh City, Korea and Vietnam."
      />
      <meta
        name="keywords"
        content="KV Trade Alliance gallery, Korea Vietnam trade photos, Seoul HCMC trade missions, bilateral trade events"
      />
      <link rel="canonical" href={`${siteUrl}/gallery`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(gallerySchema) }}
      />
      {/* Hero */}
      <section className="relative pt-28 pb-16 lg:pt-36 lg:pb-20 bg-navy">
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <p className="text-xs font-sans text-silver/50 tracking-[0.2em] uppercase mb-4">{t('galleryPage.eyebrow')}</p>
          <h1 className="font-serif text-white text-3xl sm:text-4xl lg:text-6xl font-bold leading-tight mb-4 lg:mb-5">
            {t('galleryPage.heading1')}<br />
            <span className="italic font-light text-silver">{t('galleryPage.heading2')}</span>
          </h1>
          <p className="text-white/60 font-sans text-base lg:text-lg max-w-xl">
            {t('galleryPage.heroDesc')}
          </p>
        </div>
      </section>

      {/* Filter & Grid */}
      <section ref={ref} className="bg-[#F8F9FB] py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Filter Tabs */}
          <div className="reveal flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 lg:mb-10">
            <h2 className="font-serif text-navy text-xl lg:text-2xl font-bold">
              {activeFilterLabel}
              <span className="text-navy/30 text-base font-sans font-normal ml-2">({filtered.length})</span>
            </h2>
            <div className="flex items-center gap-1 bg-white rounded-full p-1 border border-silver-light self-start sm:self-auto">
              {filters.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setActiveFilter(f.value)}
                  className={`px-4 py-2 text-xs sm:text-sm font-sans font-medium rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeFilter === f.value
                      ? 'bg-navy text-white'
                      : 'text-navy/60 hover:text-navy'
                  }`}
                >
                  {t(f.labelKey)}
                </button>
              ))}
            </div>
          </div>

          {/* Masonry Grid */}
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 lg:gap-5 space-y-4 lg:space-y-5">
            {filtered.map((item, i) => (
              <div
                key={item.id}
                className={`reveal stagger-${(i % 5) + 1} break-inside-avoid group relative rounded-xl overflow-hidden cursor-pointer`}
                onClick={() => setLightbox(item)}
              >
                <div className={`w-full ${item.height === 'tall' ? 'h-64 sm:h-80' : 'h-48 sm:h-52'} overflow-hidden`}>
                  <img
                    src={item.url}
                    alt={item.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                {/* Overlay */}
                <div className="absolute inset-0 bg-navy/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-5">
                  <p className="text-white font-serif text-lg font-semibold leading-snug mb-1">{item.title}</p>
                  <p className="text-silver text-xs font-sans flex items-center gap-1">
                    <i className="ri-map-pin-line" />{item.location}
                  </p>
                </div>
                {/* Category badge */}
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="bg-white/90 text-navy text-[10px] font-sans font-semibold px-2 py-1 rounded-full capitalize">
                    {item.category}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-navy/95 z-50 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <div
            className="relative max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center text-white/60 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl" />
            </button>
            <div className="rounded-xl overflow-hidden">
              <img
                src={lightbox.url}
                alt={lightbox.title}
                className="w-full h-auto max-h-[70vh] object-cover"
              />
            </div>
            <div className="mt-4">
              <h3 className="font-serif text-white text-xl font-semibold">{lightbox.title}</h3>
              <p className="text-silver/60 text-sm font-sans mt-1 flex items-center gap-1">
                <i className="ri-map-pin-line" />{lightbox.location}
              </p>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
